import type { PlanningApplication } from '../../types/planning';

export type ShortLetRoute = 'FULSTL' | 'CLESTL' | 'CLPSTL' | 'UNKNOWN';

export type ShortLetRecord = PlanningApplication & {
  record_type?: 'short_term_let';
  data_source?: 'short_term_let';
};

export function normalizeShortLetRecord(row: Record<string, unknown>): ShortLetRecord | null {
  const referenceNo = String(row.reference_no ?? row.reference ?? row.ref ?? '').trim();
  if (!referenceNo) return null;

  const toText = (value: unknown) => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed || undefined;
  };

  const toNumber = (value: unknown) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  return {
    reference_no: referenceNo,
    created_at: toText(row.created_at),
    description: toText(row.description),
    ai_summary: toText(row.ai_summary),
    address: toText(row.address),
    received_date: toText(row.received_date),
    validated_date: toText(row.validated_date),
    status: toText(row.status),
    decision: toText(row.decision),
    application_type: toText(row.application_type) || getShortLetRouteLabel(referenceNo),
    link: toText(row.link),
    scraped_date: toText(row.scraped_date),
    category: 'short_term_lets',
    has_application_form: Boolean(row.has_application_form),
    scale_rating: toNumber(row.scale_rating),
    latitude: toNumber(row.latitude),
    longitude: toNumber(row.longitude),
    geohash: toText(row.geohash),
    match_ref: typeof row.match_ref === 'string' || row.match_ref === null ? row.match_ref : undefined,
    group_id: typeof row.group_id === 'string' || row.group_id === null ? row.group_id : undefined,
    documents: Array.isArray(row.documents)
      ? row.documents
          .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
          .map((document) => ({
            doc_link: String(document.doc_link ?? ''),
            thumbnail_url: typeof document.thumbnail_url === 'string' ? document.thumbnail_url : undefined,
            role: typeof document.role === 'string' ? document.role : undefined,
            pdf_url: typeof document.pdf_url === 'string' ? document.pdf_url : undefined,
          }))
          .filter((document) => document.doc_link)
      : undefined,
    record_type: 'short_term_let',
    data_source: 'short_term_let',
  };
}

export function normalizeShortLetDataset(rows: unknown): ShortLetRecord[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (typeof row === 'object' && row !== null ? normalizeShortLetRecord(row as Record<string, unknown>) : null))
    .filter((row): row is ShortLetRecord => row !== null);
}

export function getShortLetRoute(referenceNo?: string | null): ShortLetRoute {
  const normalized = String(referenceNo ?? '').trim().toUpperCase();
  if (normalized.endsWith('/FULSTL')) return 'FULSTL';
  if (normalized.endsWith('/CLESTL')) return 'CLESTL';
  if (normalized.endsWith('/CLPSTL')) return 'CLPSTL';
  return 'UNKNOWN';
}

export function getShortLetRouteLabel(referenceNo?: string | null): string {
  const route = getShortLetRoute(referenceNo);
  if (route === 'FULSTL') return 'Short-Let Planning Permission';
  if (route === 'CLESTL') return 'Short-Let Certificate of Lawfulness';
  if (route === 'CLPSTL') return 'Short-Let Proposed Certificate of Lawfulness';
  return 'Short-Let Application';
}

export function extractPostcodeDistrict(address?: string | null): string | null {
  if (!address) return null;
  const match = address.match(/\b(EH\d{1,2}[A-Z]?)\s*\d[A-Z]{2}\b/i);
  return match?.[1]?.toUpperCase() || null;
}

export function extractStreetKey(address?: string | null): string | null {
  if (!address) return null;
  let value = address
    .replace(/\b(EH\d{1,2}[A-Z]?\s*\d[A-Z]{2})\b/gi, '')
    .replace(/\bEdinburgh\b/gi, '')
    .replace(/\b(Flat|Apartment|Apt|Unit|GF\d*|PF\d*|BF\d*|1F\d*|2F\d*|3F\d*|4F\d*)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!value) return null;

  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  value = parts[0] || value;
  value = value.replace(/^\d+[A-Z]?\s*/, '').trim();
  return value || null;
}

export function isRetrospectiveShortLet(record: ShortLetRecord): boolean {
  const haystack = `${record.description || ''} ${record.ai_summary || ''}`.toLowerCase();
  return haystack.includes('retrospective');
}

export function getPrimaryDate(record: ShortLetRecord): Date | null {
  const candidates = [record.received_date, record.validated_date, record.created_at, record.scraped_date];
  for (const value of candidates) {
    if (!value) continue;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

export function filterShortLetsPastYear(records: ShortLetRecord[], now = new Date()): ShortLetRecord[] {
  const cutoff = new Date(now);
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  return records.filter((record) => {
    const date = getPrimaryDate(record);
    return date ? date >= cutoff && date <= now : false;
  });
}

export function buildMonthlyCounts(records: ShortLetRecord[], months?: number, now = new Date()) {
  const buckets: Array<{ key: string; label: string; count: number }> = [];
  const anchor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

  let startDate = anchor;
  if (typeof months === 'number' && Number.isFinite(months) && months > 0) {
    startDate = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() - (months - 1), 1));
  } else {
    const datedRecords = records
      .map((record) => getPrimaryDate(record))
      .filter((date): date is Date => Boolean(date))
      .sort((left, right) => left.getTime() - right.getTime());
    if (datedRecords.length > 0) {
      const first = datedRecords[0];
      startDate = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
    }
  }

  let cursor = new Date(startDate);
  while (cursor <= anchor) {
    const key = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = cursor.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
    buckets.push({ key, label, count: 0 });
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }

  records.forEach((record) => {
    const date = getPrimaryDate(record);
    if (!date) return;
    const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    const bucket = buckets.find((entry) => entry.key === key);
    if (bucket) bucket.count += 1;
  });

  return buckets;
}

export function topCounts<T extends string>(values: T[], limit = 8) {
  const counts = new Map<T, number>();
  values.forEach((value) => {
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count || String(left.label).localeCompare(String(right.label)))
    .slice(0, limit);
}
